import { Client, type BrokerEvent } from "./client";
import { negotiate, type Media } from "./webrtc";

export interface Peer {
  phone: string;
  name?: string;
  avatar?: string;
}

export interface PeerInfo {
  callId: string;
  token?: string;
  phone?: string;
  name?: string;
  avatar?: string;
}

export interface DigiCallsConfig {
  /** DigiCalls backend, e.g. https://calls.digitalsac.io */
  baseUrl: string;
  /** Device tokens (one per WhatsApp connection). */
  tokens?: string[];
  /** token -> friendly line name. */
  deviceNames?: Record<string, string>;
  micDeviceId?: string;
  outputDeviceId?: string;
}

type CB<T> = (arg: T) => void;

function phoneOf(jidOrPhone?: string): string {
  if (!jidOrPhone) return "";
  return jidOrPhone.split("@")[0].replace(/[^0-9]/g, "");
}

// numberFromJid extracts the phone number from a JID like "5519...:60@s.whatsapp.net".
function numberFromJid(jid?: string): string {
  if (!jid) return "";
  return jid.split("@")[0].split(":")[0].replace(/[^0-9]/g, "");
}

async function getMic(deviceId?: string): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: deviceId ? { deviceId: { exact: deviceId } } : true,
  });
}

/** A call (outgoing or accepted). */
export class Call {
  private media: Media | null = null;
  private mic: MediaStream | null = null;
  private accepted = false;
  private ended = false;
  private cbAccept: CB<Call>[] = [];
  private cbEnd: CB<{ reason?: string }>[] = [];
  private cbUnanswered: CB<void>[] = [];
  private cbState: CB<string>[] = [];

  constructor(
    private readonly client: Client,
    readonly sessionId: string,
    readonly callId: string,
    readonly peer: Peer,
    readonly direction: "outbound" | "inbound",
    readonly token: string = "",
  ) {}

  onPeerAccept(cb: CB<Call>): this {
    this.cbAccept.push(cb);
    if (this.accepted) cb(this);
    return this;
  }
  onEnd(cb: CB<{ reason?: string }>): this {
    this.cbEnd.push(cb);
    return this;
  }
  onUnanswered(cb: CB<void>): this {
    this.cbUnanswered.push(cb);
    return this;
  }
  onStateChange(cb: CB<string>): this {
    this.cbState.push(cb);
    return this;
  }

  /** @internal */
  _attach(media: Media, mic: MediaStream | null): void {
    this.media = media;
    this.mic = mic;
  }
  /** @internal */
  _markAccepted(): void {
    if (this.accepted) return;
    this.accepted = true;
    this.cbAccept.forEach((c) => c(this));
    this.cbState.forEach((c) => c("accepted"));
  }
  /** @internal */
  _markEnded(reason?: string): void {
    if (this.ended) return;
    this.ended = true;
    if (!this.accepted) this.cbUnanswered.forEach((c) => c());
    this.media?.close();
    this.mic?.getTracks().forEach((t) => t.stop());
    this.cbEnd.forEach((c) => c({ reason }));
    this.cbState.forEach((c) => c("ended"));
  }

  mute(): void {
    this.mic?.getAudioTracks().forEach((t) => (t.enabled = false));
  }
  unmute(): void {
    this.mic?.getAudioTracks().forEach((t) => (t.enabled = true));
  }
  async end(): Promise<void> {
    if (this.ended) return;
    try {
      await this.client.del(`/api/sessions/${this.sessionId}/calls/${this.callId}`);
    } catch {
      /* server may already have ended it */
    }
    this._markEnded("user_ended");
  }
}

/** An incoming call offer. */
export class Offer {
  constructor(
    private readonly dev: Device,
    readonly sessionId: string,
    readonly callId: string,
    readonly peer: Peer,
    readonly token: string = "",
  ) {}

  accept(): Promise<Call> {
    return this.dev._answer(this.callId, this.peer);
  }
  async reject(): Promise<void> {
    await this.dev.client.post(`/api/sessions/${this.sessionId}/calls/${this.callId}/reject`);
  }
}

/** Internal per-token device: resolves its session, holds the SSE stream. */
class Device {
  sessionId = "";
  name = "";
  number = "";
  connected = false;
  es: EventSource | null = null;
  calls = new Map<string, Call>();

  constructor(
    readonly client: Client,
    private readonly parent: DigiCalls,
    readonly token: string = "",
  ) {}

  async start(): Promise<void> {
    const { sessions } = await this.client.get<{
      sessions: { id: string; name?: string; jid?: string; state?: string; paired?: boolean }[];
    }>("/api/sessions");
    const s = sessions?.[0];
    this.sessionId = s?.id ?? "";
    this.name = s?.name ?? "";
    this.number = numberFromJid(s?.jid);
    this.connected = s?.state === "open";
    this.es = this.client.events((ev) => this.onEvent(ev));
    this.parent._emitUpdate();
  }

  stop(): void {
    this.es?.close();
    this.es = null;
    this.calls.forEach((c) => c._markEnded("closed"));
    this.calls.clear();
  }

  private onEvent(ev: BrokerEvent): void {
    switch (ev.type) {
      case "incoming": {
        if (!ev.id) return;
        const offer = new Offer(this, ev.sessionId || this.sessionId, ev.id, { phone: phoneOf(ev.peer) }, this.token);
        this.parent._emitOffer(offer);
        break;
      }
      case "call-status": {
        const c = ev.id ? this.calls.get(ev.id) : undefined;
        if (c && ev.status === "connected") c._markAccepted();
        break;
      }
      case "call-ended": {
        if (!ev.id) return;
        const c = this.calls.get(ev.id);
        if (c) {
          c._markEnded(ev.reason);
          this.calls.delete(ev.id);
        }
        break;
      }
      case "auth-state": {
        if (ev.sessionId === this.sessionId) {
          this.connected = ev.status === "open" || (ev as { state?: string }).state === "open";
          this.parent._emitUpdate();
        }
        break;
      }
      case "session-list": {
        const list = (ev as { sessions?: { id: string; name?: string; jid?: string; state?: string }[] }).sessions;
        const mine = list?.find((x) => x.id === this.sessionId);
        if (mine) {
          this.name = mine.name ?? this.name;
          if (mine.jid) this.number = numberFromJid(mine.jid);
          this.connected = mine.state === "open";
          this.parent._emitUpdate();
        }
        break;
      }
      case "call-peer": {
        if (!ev.id) return;
        const e = ev as { peerPhone?: string; peerName?: string; peerAvatar?: string };
        this.parent._emitPeerInfo({ callId: ev.id, token: this.token, phone: e.peerPhone, name: e.peerName, avatar: e.peerAvatar });
        break;
      }
    }
  }

  /** @internal — accept an incoming call and bring up media. */
  async _answer(callId: string, peer: Peer): Promise<Call> {
    await this.client.post(`/api/sessions/${this.sessionId}/calls/${callId}/accept`);
    const mic = await getMic(this.parent.micDeviceId);
    const media = await negotiate(this.client, this.sessionId, callId, mic, this.parent.outputDeviceId, () => {
      this.calls.get(callId)?._markEnded("disconnected");
    });
    const call = new Call(this.client, this.sessionId, callId, peer, "inbound", this.token);
    call._attach(media, mic);
    call._markAccepted();
    this.calls.set(callId, call);
    return call;
  }
}

/**
 * DigiCalls — WhatsApp calls SDK:
 *   const dc = new DigiCalls({ baseUrl, tokens });
 *   dc.onOffer(o => o.accept());
 *   const { call, err } = await dc.startCall({ fromTokens, to });
 */
export class DigiCalls {
  micDeviceId?: string;
  outputDeviceId?: string;
  private devices = new Map<string, Device>();
  private offerCbs: CB<Offer>[] = [];
  private updateCbs: CB<void>[] = [];
  private peerInfoCbs: CB<PeerInfo>[] = [];

  constructor(private readonly config: DigiCallsConfig) {
    this.micDeviceId = config.micDeviceId;
    this.outputDeviceId = config.outputDeviceId;
    if (config.tokens?.length) void this.addDevices(config.tokens);
  }

  /** onUpdate fires when the device list or any device's connection changes. */
  onUpdate(cb: CB<void>): this {
    this.updateCbs.push(cb);
    return this;
  }
  /** @internal */
  _emitUpdate(): void {
    this.updateCbs.forEach((c) => c());
  }

  /** Devices currently registered in this instance (for a "Números" UI). */
  getDevices(): { token: string; sessionId: string; name: string; number: string; connected: boolean }[] {
    return [...this.devices.entries()].map(([token, d]) => ({
      token,
      sessionId: d.sessionId,
      name: this.config.deviceNames?.[token] || d.name || token.slice(0, 10),
      number: d.number,
      connected: d.connected,
    }));
  }

  async addDevices(tokens: string[]): Promise<void> {
    for (const t of tokens) {
      if (this.devices.has(t)) continue;
      const dev = new Device(new Client(this.config.baseUrl, t), this, t);
      this.devices.set(t, dev);
      try {
        await dev.start();
      } catch {
        this.devices.delete(t);
      }
    }
    this._emitUpdate();
  }

  removeDevices(tokens: string[]): void {
    for (const t of tokens) {
      this.devices.get(t)?.stop();
      this.devices.delete(t);
    }
    this._emitUpdate();
  }

  onOffer(cb: CB<Offer>): this {
    this.offerCbs.push(cb);
    return this;
  }

  /** onPeerInfo fires when the backend resolves a call's contact (phone/name/avatar). */
  onPeerInfo(cb: CB<PeerInfo>): this {
    this.peerInfoCbs.push(cb);
    return this;
  }

  /** @internal */
  _emitOffer(o: Offer): void {
    this.offerCbs.forEach((c) => c(o));
  }
  /** @internal */
  _emitPeerInfo(info: PeerInfo): void {
    this.peerInfoCbs.forEach((c) => c(info));
  }

  async startCall({ to, fromTokens }: { to: string; fromTokens?: string[] }): Promise<{ call?: Call; err?: string }> {
    const token = fromTokens?.[0] ?? [...this.devices.keys()][0];
    const dev = token ? this.devices.get(token) : undefined;
    if (!dev || !dev.sessionId) return { err: "no device available" };
    try {
      const { call: created } = await dev.client.post<{ call: { callId: string } }>(
        `/api/sessions/${dev.sessionId}/calls`,
        { phone: to },
      );
      const callId = created.callId;
      const mic = await getMic(this.micDeviceId);
      const media = await negotiate(dev.client, dev.sessionId, callId, mic, this.outputDeviceId, () => {
        dev.calls.get(callId)?._markEnded("disconnected");
      });
      const call = new Call(dev.client, dev.sessionId, callId, { phone: to }, "outbound", token);
      call._attach(media, mic);
      dev.calls.set(callId, call);
      return { call };
    } catch (e) {
      return { err: (e as Error).message };
    }
  }

  async getMultimediaDevices(): Promise<{ microphones: MediaDeviceInfo[]; speakers: MediaDeviceInfo[] }> {
    const list = await navigator.mediaDevices.enumerateDevices();
    return {
      microphones: list.filter((d) => d.kind === "audioinput"),
      speakers: list.filter((d) => d.kind === "audiooutput"),
    };
  }

  setMic(deviceId?: string): void {
    this.micDeviceId = deviceId;
  }
  setOutput(deviceId?: string): void {
    this.outputDeviceId = deviceId;
  }

  close(): void {
    this.devices.forEach((d) => d.stop());
    this.devices.clear();
  }
}

export default DigiCalls;
